"use client"

import { useEffect, useRef } from "react"
import { Box } from "@forestsync/ui"
import {
  createChart,
  createTextWatermark,
  LineSeries,
  type IChartApi,
  type ISeriesApi,
  type IPriceLine,
  type LineData,
  type Time,
} from "lightweight-charts"
import type { OrderBook } from "@/lib/queries"
import type { TradeFill } from "./TradeBlotter"
import { HeatmapPrimitive, type Snapshot } from "./bookmap/HeatmapPrimitive"
import { BubblePrimitive, type FillBucket } from "./bookmap/BubblePrimitive"
import { OverlayPrimitive } from "./bookmap/OverlayPrimitive"
import { VolumeProfilePrimitive } from "./bookmap/VolumeProfilePrimitive"

type OhlcvBar = { time: number; open: number; high: number; low: number; close: number; volume: number }

interface BookmapHeatmapProps {
  orderBook: OrderBook
  fills: TradeFill[]
  precision: number
  timeframe?: string
  showVwap?: boolean
  showVolumeProfile?: boolean
  onCrosshairMove?: (bar: OhlcvBar | null) => void
}

const MAX_SNAPSHOTS = 60
const SNAPSHOT_MIN_INTERVAL_MS = 3000
const CHART_H = 280

// Seconds per timeframe key — used as the OHLCV bucket interval and visible range
const TIMEFRAME_SECS: Record<string, number> = {
  "1m": 60,
  "3m": 3 * 60,
  "5m": 5 * 60,
  "15m": 15 * 60,
  "30m": 30 * 60,
  "1h": 3600,
  "4h": 4 * 3600,
  "12h": 12 * 3600,
  "1d": 86400,
  "1w": 7 * 86400,
  "1M": 30 * 86400,
}

function priceBucket(price: number, precision: number): number {
  return parseFloat((Math.floor(price / precision) * precision).toFixed(8))
}

function bucketize(
  levels: Array<{ price: number; volume: number }>,
  precision: number,
): Map<number, number> {
  const map = new Map<number, number>()
  for (const l of levels) {
    const k = priceBucket(l.price, precision)
    map.set(k, (map.get(k) ?? 0) + l.volume)
  }
  return map
}

function bucketFills(fills: TradeFill[], bucketMs: number): FillBucket[] {
  if (fills.length === 0) return []
  const map = new Map<number, { wPrice: number; totalVol: number; bidVol: number; aggressorCounts: Map<number, number> }>()
  for (const f of fills) {
    const t = Math.floor(f.time.getTime() / bucketMs) * bucketMs
    const b = map.get(t) ?? { wPrice: 0, totalVol: 0, bidVol: 0, aggressorCounts: new Map() }
    b.wPrice += f.price * f.volume
    b.totalVol += f.volume
    if (f.side === 1) b.bidVol += f.volume
    if (f.aggressorSide != null) {
      b.aggressorCounts.set(f.aggressorSide, (b.aggressorCounts.get(f.aggressorSide) ?? 0) + 1)
    }
    map.set(t, b)
  }
  return Array.from(map.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([time, b]) => {
      let dominantAggressorSide: number | null = null
      if (b.aggressorCounts.size > 0) {
        dominantAggressorSide = [...b.aggressorCounts.entries()].reduce((best, cur) => cur[1] > best[1] ? cur : best)[0]
      }
      return {
        time,
        price: b.totalVol > 0 ? b.wPrice / b.totalVol : 0,
        volume: b.totalVol,
        side: b.bidVol >= b.totalVol / 2 ? 1 : 2,
        aggressorSide: dominantAggressorSide,
      }
    })
}

export function BookmapHeatmap({ orderBook, fills, precision, timeframe = "1h", showVwap = true, showVolumeProfile = false, onCrosshairMove }: BookmapHeatmapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<ISeriesApi<"Line"> | null>(null)
  const heatmapPrimRef = useRef<HeatmapPrimitive | null>(null)
  const bubblePrimRef = useRef<BubblePrimitive | null>(null)
  const overlayPrimRef = useRef<OverlayPrimitive | null>(null)
  const vwapSeriesRef = useRef<ISeriesApi<"Line"> | null>(null)
  const volumeProfilePrimRef = useRef<VolumeProfilePrimitive | null>(null)
  const priceLineRef = useRef<IPriceLine | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const snapshotsRef = useRef<Snapshot[]>([])
  const lastSnapshotTimeRef = useRef<number>(0)
  const fillBucketsRef = useRef<FillBucket[]>([])
  const lastTimeframeRef = useRef(timeframe)
  const targetLogicalRangeRef = useRef<{ from: number; to: number } | null>(null)
  const ohlcvMapRef = useRef<Map<number, OhlcvBar>>(new Map())
  const bucketSecsRef = useRef(TIMEFRAME_SECS[timeframe] ?? TIMEFRAME_SECS["1h"])
  const onCrosshairMoveRef = useRef(onCrosshairMove)
  onCrosshairMoveRef.current = onCrosshairMove

  // Init chart and primitives once on mount (precision is stable per market)
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight || CHART_H,
      layout: { background: { color: "#0d1117" }, textColor: "#8ca0b5" },
      grid: { vertLines: { color: "#1a2332" }, horzLines: { color: "#1a2332" } },
      crosshair: { mode: 0 },
      rightPriceScale: { borderColor: "#2a3a4a" },
      timeScale: { borderColor: "#2a3a4a", timeVisible: true, secondsVisible: true },
    })
    chartRef.current = chart

    // Carbonomex branding watermark (replaces LW-Charts v5 default attribution)
    createTextWatermark(chart.panes()[0], {
      horzAlign: "left",
      vertAlign: "bottom",
      lines: [{ text: "Carbonomex", color: "rgba(255, 255, 255, 0.10)", fontSize: 13 }],
    })

    const series = chart.addSeries(LineSeries, {
      color: "rgba(0,0,0,0)",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    })
    seriesRef.current = series

    const heatmap = new HeatmapPrimitive({ snapshots: [], globalMaxVol: 1, precision })
    const bubble = new BubblePrimitive({ buckets: [] })
    const overlay = new OverlayPrimitive({ bestBid: null, bestAsk: null, lastPrice: null })

    const volProfile = new VolumeProfilePrimitive({ volumeByPrice: new Map(), maxVolume: 1, precision, visible: false })

    series.attachPrimitive(heatmap)
    series.attachPrimitive(bubble)
    series.attachPrimitive(overlay)
    series.attachPrimitive(volProfile)

    heatmapPrimRef.current = heatmap
    bubblePrimRef.current = bubble
    overlayPrimRef.current = overlay
    volumeProfilePrimRef.current = volProfile

    // VWAP line — overlaid on main price scale
    const vwapSeries = chart.addSeries(LineSeries, {
      color: "rgba(255, 200, 0, 0.85)",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: true,
      crosshairMarkerVisible: false,
      title: "VWAP",
    })
    vwapSeriesRef.current = vwapSeries

    // Floating tooltip for crosshair hover
    const tooltip = document.createElement("div")
    tooltip.style.cssText = [
      "position:absolute",
      "z-index:100",
      "pointer-events:none",
      "background:rgba(13,17,23,0.92)",
      "border:1px solid #2a3a4a",
      "border-radius:4px",
      "padding:4px 8px",
      "font-size:11px",
      "color:#c9d1d9",
      "display:none",
      "white-space:nowrap",
    ].join(";")
    container.style.position = "relative"
    container.appendChild(tooltip)
    tooltipRef.current = tooltip

    chart.subscribeCrosshairMove((param) => {
      if (!param.point || !param.time || !tooltipRef.current) {
        if (tooltipRef.current) tooltipRef.current.style.display = "none"
        onCrosshairMoveRef.current?.(null)
        return
      }

      const crosshairPrice = seriesRef.current?.coordinateToPrice(param.point.y) ?? null

      // Detect if crosshair is inside a bubble — snap price+time to the fill's actual values
      let hitBucket: FillBucket | null = null
      let bestDist = Infinity
      for (const bucket of fillBucketsRef.current) {
        const bx = chart.timeScale().timeToCoordinate(Math.floor(bucket.time / 1000) as Time)
        const by = seriesRef.current?.priceToCoordinate(bucket.price) ?? null
        if (bx === null || by === null) continue
        const visualRadius = Math.max(5, Math.min(Math.sqrt(bucket.volume) * 1.3, 32))
        const snapRadius = visualRadius * 1.5
        const dist = Math.sqrt((param.point.x - bx) ** 2 + (param.point.y - by) ** 2)
        if (dist <= snapRadius && dist < bestDist) {
          bestDist = dist
          hitBucket = bucket
        }
      }

      // Use bubble's exact price + time when hovering inside it; otherwise use crosshair position
      const displayPrice = hitBucket !== null ? hitBucket.price : crosshairPrice
      const displayDate = hitBucket !== null
        ? new Date(hitBucket.time)
        : new Date((param.time as number) * 1000)

      const hh = String(displayDate.getUTCHours()).padStart(2, "0")
      const mm = String(displayDate.getUTCMinutes()).padStart(2, "0")
      const ss = String(displayDate.getUTCSeconds()).padStart(2, "0")
      const timeStr = `${hh}:${mm}:${ss}`

      if (displayPrice === null) {
        tooltipRef.current.innerHTML = `<span style="color:#8ca0b5">${timeStr}</span>`
      } else {
        let fillLabel: string | null = null
        if (hitBucket !== null) {
          const isBuy = hitBucket.aggressorSide != null ? hitBucket.aggressorSide === 1 : hitBucket.side === 2
          fillLabel = `${isBuy ? "ASK" : "BID"} · ${hitBucket.volume.toFixed(1)}t`
        }

        const priceStr = `€<strong>${displayPrice.toFixed(2)}</strong>`
        tooltipRef.current.innerHTML = fillLabel
          ? `${priceStr} · <span style="color:#8ca0b5">${fillLabel} · ${timeStr}</span>`
          : `${priceStr} · <span style="color:#8ca0b5">${timeStr}</span>`
      }

      tooltipRef.current.style.display = "block"
      const x = param.point.x + 12
      const y = param.point.y - 28
      tooltipRef.current.style.left = `${x}px`
      tooltipRef.current.style.top = `${y}px`

      const bucketSec = Math.floor((param.time as number) / bucketSecsRef.current) * bucketSecsRef.current
      onCrosshairMoveRef.current?.(ohlcvMapRef.current.get(bucketSec) ?? null)
    })

    const ro = new ResizeObserver((entries) => {
      if (chartRef.current) {
        const { width, height } = entries[0].contentRect
        chartRef.current.applyOptions({ width, height: Math.max(height || CHART_H, 80) })
      }
    })
    ro.observe(container)

    return () => {
      ro.disconnect()
      tooltip.remove()
      chart.remove()
      chartRef.current = null
      seriesRef.current = null
      vwapSeriesRef.current = null
      heatmapPrimRef.current = null
      bubblePrimRef.current = null
      overlayPrimRef.current = null
      volumeProfilePrimRef.current = null
      priceLineRef.current = null
      tooltipRef.current = null
    }
  // precision is stable per market — safe as dep
  }, [precision])

  // Pre-seed 15 synthetic snapshots on mount so heatmap is never black on first load
  useEffect(() => {
    if (orderBook.bids.length === 0 && orderBook.asks.length === 0) return
    const now = Date.now()
    const bids = bucketize(orderBook.bids, precision)
    const asks = bucketize(orderBook.asks, precision)
    snapshotsRef.current = Array.from({ length: 15 }, (_, i) => ({
      time: now - (14 - i) * SNAPSHOT_MIN_INTERVAL_MS,
      bids,
      asks,
    }))
    lastSnapshotTimeRef.current = now
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Mount only — intentional stale closure for initial snapshot

  // Update primitives and anchor series on every orderBook / fills / timeframe change
  useEffect(() => {
    const series = seriesRef.current
    const chart = chartRef.current
    if (!series || !chart) return

    // Compute time constants first — needed for both snapshot reset and anchor building
    const bucketSecs = TIMEFRAME_SECS[timeframe] ?? TIMEFRAME_SECS["1h"]
    bucketSecsRef.current = bucketSecs
    const visibleSecs = bucketSecs * 200
    const nowMs = Date.now()
    const nowSec = Math.floor(nowMs / 1000)
    const rightEdgeSec = nowSec + bucketSecs * 3

    // Track whether timeframe changed so we can reset state and re-pin the range
    const timeframeDidChange = lastTimeframeRef.current !== timeframe
    if (timeframeDidChange) {
      lastTimeframeRef.current = timeframe
      const bids = bucketize(orderBook.bids, precision)
      const asks = bucketize(orderBook.asks, precision)
      snapshotsRef.current = Array.from({ length: 5 }, (_, i) => ({
        time: nowMs - (4 - i) * SNAPSHOT_MIN_INTERVAL_MS,
        bids,
        asks,
      }))
      lastSnapshotTimeRef.current = nowMs
    } else if (nowMs - lastSnapshotTimeRef.current >= SNAPSHOT_MIN_INTERVAL_MS) {
      lastSnapshotTimeRef.current = nowMs
      const snap: Snapshot = {
        time: nowMs,
        bids: bucketize(orderBook.bids, precision),
        asks: bucketize(orderBook.asks, precision),
      }
      const cur = snapshotsRef.current
      snapshotsRef.current = cur.length >= MAX_SNAPSHOTS ? [...cur.slice(1), snap] : [...cur, snap]
    }

    const snaps = snapshotsRef.current
    let globalMaxVol = 1
    for (const snap of snaps) {
      for (const v of snap.bids.values()) if (v > globalMaxVol) globalMaxVol = v
      for (const v of snap.asks.values()) if (v > globalMaxVol) globalMaxVol = v
    }

    // Compute price range for autoscale — covers order book levels + fills + padding
    const allPriceValues: number[] = [
      ...orderBook.bids.map((b) => b.price),
      ...orderBook.asks.map((a) => a.price),
      ...fills.map((f) => f.price),
    ]
    if (allPriceValues.length > 0) {
      const rawMin = Math.min(...allPriceValues)
      const rawMax = Math.max(...allPriceValues)
      const pad = (rawMax - rawMin) * 0.15 + precision * 4
      series.applyOptions({
        autoscaleInfoProvider: () => ({
          priceRange: { minValue: rawMin - pad, maxValue: rawMax + pad },
          margins: { above: 0, below: 0 },
        }),
      })
    }

    // Anchor range spans all loaded fills so BubblePrimitive can resolve any timestamp
    const earliestFillSec = fills.length > 0
      ? Math.floor(Math.min(...fills.map((f) => f.time.getTime())) / 1000)
      : nowSec - visibleSecs
    const anchorStartSec = Math.min(earliestFillSec, nowSec - visibleSecs)
    // timeframeStartSec is the left edge of the visible viewport
    const timeframeStartSec = nowSec - visibleSecs
    const midPrice =
      allPriceValues.length > 0
        ? (Math.min(...allPriceValues) + Math.max(...allPriceValues)) / 2
        : 0

    // Build anchor series: uniform skeleton + snapshot timestamps at the right edge.
    // Snapshot timestamps MUST be in the series — LW-charts timeToCoordinate() returns
    // null for times not present as exact bar entries (no interpolation in v5).
    // We use setVisibleLogicalRange (bar-index space) to control zoom, which is
    // density-independent and works correctly regardless of snapshot accumulation.
    const totalSpan = rightEdgeSec - anchorStartSec
    const anchorInterval = Math.max(3, Math.floor(totalSpan / 150))
    const anchorPoints: LineData[] = []
    for (let t = anchorStartSec; t <= rightEdgeSec; t += anchorInterval) {
      anchorPoints.push({ time: t as Time, value: midPrice })
    }
    // Right edge guarantee (setVisibleLogicalRange to = last index)
    if ((anchorPoints[anchorPoints.length - 1]?.time as number) < rightEdgeSec) {
      anchorPoints.push({ time: rightEdgeSec as Time, value: midPrice })
    }
    // Add snapshot timestamps so timeToCoordinate() resolves for HeatmapPrimitive
    for (const s of snaps) {
      anchorPoints.push({ time: Math.floor(s.time / 1000) as Time, value: midPrice })
    }

    // Dedupe by time + sort ascending (LW-charts requirement)
    const deduped = Array.from(
      new Map(anchorPoints.map((p) => [p.time, p])).values(),
    ).sort((a, b) => Number(a.time) - Number(b.time))

    if (deduped.length >= 2 && midPrice > 0) {
      series.setData(deduped)

      // Compute logical range for the desired visible window.
      // Using logical (bar-index) space avoids distortion from dense snapshot bars:
      // setVisibleRange works in time space where N dense bars = N bar widths of
      // canvas, but setVisibleLogicalRange is immune to bar-count density.
      const fromTime = fills.length > 0 ? earliestFillSec : nowSec - visibleSecs
      const fromIdx = deduped.findIndex(p => Number(p.time) >= fromTime)
      const logicalFrom = fromIdx <= 0 ? 0 : fromIdx - 0.5
      const logicalTo = deduped.length - 1 + 0.5
      const logicalRange = { from: logicalFrom, to: logicalTo }

      const wasFirstLoad = targetLogicalRangeRef.current === null
      targetLogicalRangeRef.current = logicalRange

      // On interval switch or first load, pin the right edge to real-time.
      // scrollToRealTime() is reliable regardless of anchor bar density;
      // setVisibleLogicalRange can mis-calculate when dense snapshot bars change the index count.
      if (timeframeDidChange || wasFirstLoad) {
        chart.timeScale().scrollToRealTime()
      }
    }

    const bestBid = orderBook.bids.length > 0 ? Math.max(...orderBook.bids.map((b) => b.price)) : null
    const bestAsk = orderBook.asks.length > 0 ? Math.min(...orderBook.asks.map((a) => a.price)) : null
    const lastPrice = fills.length > 0 ? fills[fills.length - 1].price : null

    // BubblePrimitive: raw fills sorted chronologically. Side comes from the
    // DB side field: ASK (2) = buyer was aggressor = green, BID (1) = seller = red.
    const rawFillBuckets: FillBucket[] = fills
      .filter((f) => Math.floor(f.time.getTime() / 1000) >= timeframeStartSec)
      .sort((a, b) => a.time.getTime() - b.time.getTime())
      .map((f) => ({
        time: f.time.getTime(),
        price: f.price,
        volume: f.volume,
        side: f.side,
        aggressorSide: f.aggressorSide ?? null,
      }))
    fillBucketsRef.current = rawFillBuckets

    // Build OHLCV map for crosshair callback (sorted fills → open=first, close=last per bucket)
    const ohlcvMap = new Map<number, OhlcvBar>()
    const sortedFills = [...fills].sort((a, b) => a.time.getTime() - b.time.getTime())
    for (const f of sortedFills) {
      const bucketSec = Math.floor(f.time.getTime() / 1000 / bucketSecs) * bucketSecs
      const existing = ohlcvMap.get(bucketSec)
      if (!existing) {
        ohlcvMap.set(bucketSec, { time: bucketSec, open: f.price, high: f.price, low: f.price, close: f.price, volume: f.volume })
      } else {
        existing.high = Math.max(existing.high, f.price)
        existing.low = Math.min(existing.low, f.price)
        existing.close = f.price
        existing.volume += f.volume
      }
    }
    ohlcvMapRef.current = ohlcvMap

    heatmapPrimRef.current?.updateParams({ snapshots: snaps, globalMaxVol, precision })
    bubblePrimRef.current?.updateParams({ buckets: rawFillBuckets })
    overlayPrimRef.current?.updateParams({ bestBid, bestAsk, lastPrice })

    // Compute volume profile — aggregate all fills by price bucket
    const vpVolByPrice = new Map<number, number>()
    let vpMaxVol = 1
    for (const f of fills) {
      const k = priceBucket(f.price, precision)
      const v = (vpVolByPrice.get(k) ?? 0) + f.volume
      vpVolByPrice.set(k, v)
      if (v > vpMaxVol) vpMaxVol = v
    }
    volumeProfilePrimRef.current?.updateParams({ volumeByPrice: vpVolByPrice, maxVolume: vpMaxVol })

    // Current price line — update or create on the anchor series
    if (lastPrice !== null) {
      if (priceLineRef.current) {
        priceLineRef.current.applyOptions({ price: lastPrice })
      } else {
        priceLineRef.current = series.createPriceLine({
          price: lastPrice,
          color: "rgba(255, 255, 255, 0.75)",
          lineWidth: 1,
          lineStyle: 0,
          axisLabelVisible: true,
          title: "",
        })
      }
    }

    // VWAP line — cumulative (price × volume) / cumulative volume over visible fills
    if (vwapSeriesRef.current) {
      const visibleFills = fills
        .filter((f) => Math.floor(f.time.getTime() / 1000) >= timeframeStartSec)
        .sort((a, b) => a.time.getTime() - b.time.getTime())
      let cumPV = 0
      let cumV = 0
      const vwapData: LineData[] = visibleFills
        .filter((f) => f.volume > 0)
        .map((f) => {
          cumPV += f.price * f.volume
          cumV += f.volume
          return { time: Math.floor(f.time.getTime() / 1000) as Time, value: cumPV / cumV }
        })
      // Dedupe by time (take last value for duplicate timestamps)
      const vwapDeduped = Array.from(
        new Map(vwapData.map((p) => [p.time, p])).values()
      ).sort((a, b) => Number(a.time) - Number(b.time))
      vwapSeriesRef.current.setData(vwapDeduped.length >= 2 ? vwapDeduped : [])
    }
  }, [orderBook, fills, precision, timeframe])

  // Backup: apply the logical range 50ms after timeframe changes.
  // Backup: ensure right edge is pinned 50ms after timeframe changes,
  // in case the data effect ran before the order book was loaded.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!chartRef.current) return
      chartRef.current.timeScale().scrollToRealTime()
    }, 50)
    return () => clearTimeout(timer)
  }, [timeframe])

  // Toggle VWAP visibility
  useEffect(() => {
    if (vwapSeriesRef.current) {
      vwapSeriesRef.current.applyOptions({ visible: showVwap })
    }
  }, [showVwap])

  // Toggle volume profile visibility
  useEffect(() => {
    volumeProfilePrimRef.current?.updateParams({ visible: showVolumeProfile })
  }, [showVolumeProfile])

  return (
    <Box style={{ background: "#0d1117", borderRadius: 4, overflow: "hidden", width: "100%", height: "100%" }}>
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
    </Box>
  )
}
